import React, { FunctionComponent, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled from 'styled-components/native'

import { LYRA_DISPLAY_LANGUAGES } from '@lyrahealth-inc/shared-app-logic/src/features/intl/constants'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { Modal } from '../../organisms/modal/Modal'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface RightToLeftLanguageAlertModalProps {
  selectedLocale: string
  onShowClassicVersionPress: () => void
  onUpdateLanguagePress: () => void
  visible: boolean
}

const ModalBodyContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMobileSized && { padding: theme.spacing['16px'] }),
}))

const TitleAndXButtonContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const StyledBodyText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
  marginBottom: theme.spacing['32px'],
}))

const CTAContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'end',
  gap: theme.spacing['16px'],
  ...(theme.breakpoints.isMobileSized && {
    flexDirection: 'column-reverse',
    gap: theme.spacing['8px'],
  }),
}))

export const RightToLeftLanguageAlertModal: FunctionComponent<RightToLeftLanguageAlertModalProps> = ({
  selectedLocale,
  onShowClassicVersionPress,
  onUpdateLanguagePress,
  visible,
}) => {
  const selectedLanguage = LYRA_DISPLAY_LANGUAGES[selectedLocale].name
  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const openBottomSheet = useCallback(() => {
    bottomSheetRef.current?.expand()
  }, [])
  const closeBottomSheet = useCallback(() => {
    bottomSheetRef?.current?.close()
  }, [])

  const snapPoints = [300]

  const modalBody = (
    <ModalBodyContainer testID={tID('RightToLeftLanguageAlertModal')}>
      <TitleAndXButtonContainer>
        <Title
          text={
            <FormattedMessage
              defaultMessage='Choose another language'
              description='Title for modal that alerts user to select another language'
            />
          }
          size={TitleSize.MEDIUM}
        />
        <PressableOpacity onPress={onUpdateLanguagePress}>
          <XIcon size={24} />
        </PressableOpacity>
      </TitleAndXButtonContainer>
      <StyledBodyText
        text={
          <FormattedMessage
            defaultMessage='Your preferred language is currently unavailable for this page. You can select another language or explore our classic version in {language}.'
            description='Description for modal that alerts user the language they have selected is unavailable for this page'
            values={{
              language: selectedLanguage,
            }}
          />
        }
        size={BodyTextSize.DEFAULT}
      />
      <CTAContainer>
        <PrimaryButton
          text={
            <FormattedMessage
              defaultMessage='Show classic version'
              description='Button text to take user to the old version of the home page in the user’s preferred language'
            />
          }
          onPress={onShowClassicVersionPress}
          testID={tID('RightToLeftLanguageAlertModal-showClassicVersionButton')}
          fullWidth
        />
      </CTAContainer>
    </ModalBodyContainer>
  )
  return (
    <Modal
      modalContents={modalBody}
      onRequestClose={onUpdateLanguagePress}
      onCloseEnd={() => {
        onUpdateLanguagePress()
        closeBottomSheet()
      }}
      visible={visible}
      useWebPortalForBottomSheet
      bottomSheetRef={bottomSheetRef}
      onLayout={openBottomSheet}
      snapPoints={snapPoints}
    />
  )
}
